import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { FbCheckboxCompositeWidget } from '../../../models';
import { Widget } from '../../../models/form-widget.model';
import { WidgetBase } from '../../../models/widget-base';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';

@Component({
  selector: 'app-fb-checkbox-composite',
  templateUrl: './fb-checkbox-composite.component.html',
  styleUrls: ['./fb-checkbox-composite.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TokenToDivPipe,
    MatCheckboxModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbCheckboxCompositeComponent
  extends WidgetBase
  implements OnInit, OnDestroy, DoCheck, OnChanges
{
  public columns;
  private _payloadOriginal;
  private checkInterval: any;

  @Input()
  public override set payload(value) {
    this._payload = value;
    this.makeColumns();
    this._payloadOriginal = JSON.stringify(this.payload);
    this.ready.next(true);
  }

  public override get payload() {
    return this._payload;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private injector: Injector,
    private _cd: ChangeDetectorRef,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.checkInterval = setInterval(() => {
      const currentPayload = JSON.stringify(this._payload);
      if (currentPayload !== this._payloadOriginal) {
        this._payloadOriginal = currentPayload;
        this.makeColumns();
      }
    }, 1000);
  }

  override ngOnDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    super.ngOnDestroy();
  }

  ngDoCheck() {}

  ngOnChanges(changes: SimpleChanges | any) {}

  public makeColumns() {
    const items = this.payload.properties.value.value;
    const totalItems = items.length;
    const requestedCols = (this.payload.properties.columns || {}).value || 1;
    const cols = Math.max(1, Math.min(requestedCols, totalItems || 1));

    const result: any[] = Array.from({ length: cols }, () => []);
    // Distribute row-wise so first row preserves original order across columns.
    for (let i = 0; i < totalItems; i++) {
      result[i % cols].push(items[i]);
    }

    this.columns = result;
  }

  public getStyles() {
    const cols = Math.max(
      1,
      this.columns?.length || (this.payload.properties.columns || {}).value || 1,
    );
    const styles = [
      'flex: 0 0 ' + 100 / cols + '%',
      'width: ' + 100 / cols + '%',
      'min-width: ' + 100 / cols + '%',
      'max-width: ' + 100 / cols + '%',
    ];

    return this.sanitizeStyle(styles.join(';'));
  }

  public sanitizeStyle(unsafeStyle: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(unsafeStyle);
  }

  override isValid() {
    return new FbCheckboxCompositeWidget(this.payload).isValid();
  }

  public trackColumn(index: number): number {
    return index;
  }

  public trackItem(index: number, item: any): string | number {
    return item.properties?.text?.value || index;
  }
}
